import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './AuthProvider';

/**
 * Route gate. Order matters:
 *   1. Wait while the session/profile is resolving (avoids a login flash).
 *   2. No session → /login.
 *   3. Session but no active profile → /access-pending (deactivation is
 *      effective on the next page load, even though the token is still valid).
 *   4. Optional role gate → bounce to /dashboard.
 */
export function ProtectedRoute({
  children,
  allowRoles,
}: {
  children: ReactNode;
  allowRoles?: Array<'admin' | 'member'>;
}) {
  const { user, profile, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div className="centered">Loading…</div>;
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!profile || !profile.is_active) {
    return <Navigate to="/access-pending" replace />;
  }
  if (allowRoles && !allowRoles.includes(profile.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}
